import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { API, fileUrl } from "../lib/api";
import axios from "axios";
import { CheckCircle2, Loader2, Download, Mail, XCircle, Home, PlayCircle, FileText } from "lucide-react";
import { Seo } from "../components/Seo";

export default function CourseAccess() {
  const [state, setState] = useState("checking"); // checking | ready | failed | timeout
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(0);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("failed"); return; }
    if (attempt >= 8) { setState("timeout"); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        const a = await axios.get(`${API}/access/${sessionId}`);
        setData(a.data);
        setState(a.data.status === "ready" ? "ready" : "pending");
        return;
      }
      if (res.data.status === "expired") { setState("failed"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  if (state === "pending") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 md:py-20 text-center" data-testid="access-pending">
        <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4"><CheckCircle2 size={56} strokeWidth={2} /></span>
        <h1 className="font-heading text-4xl font-extrabold text-[#1A1A1A] mt-6">Tack för ditt köp!</h1>
        <p className="text-xl text-[#4A4A4A] mt-4">
          Vi förbereder ditt material <strong className="text-[#1A1A1A]">{data?.title}</strong> just nu.
        </p>
        <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-6 mt-8 text-left">
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-[#1A1A1A]"><Mail size={22} strokeWidth={2.5} /> Vi hör av oss så snart det är klart</p>
          <p className="text-lg text-[#4A4A4A] mt-2">
            Så snart materialet är klart skickar vi din personliga länk till <strong className="text-[#1A1A1A]">{data?.email}</strong>.
            Du kan även spara den här sidan och komma tillbaka – dina videor och PDF-guide dyker upp här automatiskt.
          </p>
        </div>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-8">
          <Home size={22} strokeWidth={2.5} /> Tillbaka till start
        </Link>
      </div>
    );
  }

  if (state === "checking") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="access-checking">
        <Loader2 className="animate-spin mx-auto text-[#0056D2]" size={56} />
        <h1 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-6">Vi förbereder din kurs...</h1>
        <p className="text-lg text-[#4A4A4A] mt-3">Det tar bara några sekunder. Vänligen vänta.</p>
      </div>
    );
  }

  if (state === "failed" || state === "timeout") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="access-failed">
        <span className="inline-flex bg-red-50 text-[#D32F2F] rounded-full p-4"><XCircle size={64} strokeWidth={2} /></span>
        <h1 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-6">
          {state === "timeout" ? "Det tar längre tid än väntat" : "Vi kunde inte hitta din betalning"}
        </h1>
        <p className="text-lg text-[#4A4A4A] mt-3">Kolla din e-post för din unika länk, eller kontakta oss så hjälper vi dig.</p>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-8">
          <Home size={22} strokeWidth={2.5} /> Tillbaka till start
        </Link>
      </div>
    );
  }

  const chapters = data.chapters || [];
  const active = chapters[current];

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-14" data-testid="access-ready">
      <Seo title="Din kurs" path="/tillgang" noindex />
      <div className="flex items-center gap-3">
        <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-2"><CheckCircle2 size={28} strokeWidth={2.5} /></span>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#1A1A1A]">{data.title}</h1>
      </div>
      <p className="text-lg text-[#4A4A4A] mt-3">Välkommen! Här är din kurs. Tryck på ett kapitel för att titta – i din egen takt.</p>

      {/* Video player */}
      {active && (
        <div className="mt-8">
          <video
            key={active.path}
            data-testid="course-video"
            controls
            preload="metadata"
            className="w-full rounded-xl border-2 border-gray-300 bg-black"
            src={fileUrl(active.path)}
          >
            Din webbläsare kan inte spela upp videon.
          </video>
          <p className="font-heading text-xl font-bold text-[#1A1A1A] mt-3">{active.title}</p>
        </div>
      )}

      {/* Chapters */}
      <div className="mt-6 space-y-3">
        {chapters.map((c, i) => (
          <button key={i} data-testid={`chapter-${i}`} onClick={() => { setCurrent(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className={`flex items-center gap-4 w-full text-left min-h-[64px] px-5 rounded-lg border-2 transition-colors ${i === current ? "border-[#0056D2] bg-blue-50" : "border-gray-300 bg-white hover:bg-gray-50"}`}>
            <PlayCircle size={30} strokeWidth={2} className={i === current ? "text-[#0056D2]" : "text-[#4A4A4A]"} />
            <span className="text-lg font-semibold text-[#1A1A1A]">{c.title}</span>
          </button>
        ))}
      </div>

      {/* PDF + email */}
      <div className="bg-white border-2 border-gray-300 rounded-xl p-6 md:p-8 mt-8">
        <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><FileText size={26} strokeWidth={2.5} /> Din PDF-guide</h2>
        <p className="text-lg text-[#4A4A4A] mt-2">Den skriftliga guiden kan du skriva ut eller läsa på skärmen.</p>
        <a href={`${API}/download/${sessionId}`} data-testid="download-pdf"
          className="inline-flex items-center justify-center gap-3 min-h-[64px] px-8 mt-4 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors">
          <Download size={26} strokeWidth={2.5} /> Ladda ner PDF-guiden
        </a>
      </div>

      <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-6 mt-6">
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-[#1A1A1A]"><Mail size={22} strokeWidth={2.5} /> Spara din länk</p>
        <p className="text-lg text-[#4A4A4A] mt-2">
          Vi har skickat en unik länk till <strong className="text-[#1A1A1A]">{data.email}</strong>.
          Använd den när du vill komma tillbaka till kursen – du behöver inget lösenord.
        </p>
      </div>

      <div className="text-center mt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
          <Home size={22} strokeWidth={2.5} /> Tillbaka till start
        </Link>
      </div>
    </div>
  );
}
